import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import CommonModal from '../../../../shared/components/modal/common-modal'
import { useTranslation } from 'react-i18next'
import { Col, Flex, Form, Input, notification, Radio, Row, Space, Spin, Typography, Upload } from 'antd'
import { Controller, useForm, useWatch } from 'react-hook-form'
import './AccountSetupUpload.scss'
import {
  EnumSelectAction,
  fileExtension,
  IFormUpload,
  IResImportFile,
  useClientSelectData,
  useFormRules,
  useImportFileTemplate,
  validateFile,
} from './useAccountSetupUpload'
import { ErrorMessage } from '@hookform/error-message'
import { DownloadSuccessAccountIcon, UploadAccountIcon } from '../../../../../assets/icons'
import Dragger from 'antd/es/upload/Dragger'
import {
  colorBgError,
  colorBgErrorBold,
  colorBorder,
  colorText,
  colorTextSelected,
  colorTextSubtle,
  colorTextSubtlest,
} from '../../../../themes/primaryThemeDefault'
import { LoadingOutlined } from '@ant-design/icons'
import saveAs from 'file-saver'
import { getccountTemplate } from 'src/app/shared/services/AccountService'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import DownloadWhiteIcon from 'src/assets/images/download-white.png'
import CustomSelect from 'src/app/shared/components/common/select'

interface AccountSetupUploadProps {
  onViewErrorResults?: (data: IResImportFile) => any
  ref: any
  setIsResetView?: (value: boolean) => void
}

const AccountSetupUpload: React.FC<AccountSetupUploadProps> = forwardRef(
  ({ onViewErrorResults, setIsResetView }, ref) => {
    const { t } = useTranslation()
    const [isShowModal, setIsShowModal] = useState<boolean>(false)
    const { isLoading: isLoadingClient, clientOptions, getClientOptions } = useClientSelectData()
    const [templateFileName, setTemplateFileName] = useState<string>('')
    const [downloadFileURL, setDownloadFileURL] = useState<string>('')
    const [isFileContainsInvalidData, setIsFileContainsInvalidData] = useState<boolean>(false)
    const rules = useFormRules()
    const [loading, setLoading] = useState<boolean>(false)
    const [isLoadingUploadLocal, setIsLoadingUploadLocal] = useState<boolean>(false)
    const { isLoading: isLoadingSubmitForm, resImportFileTemplate, importFileAccountTemplate } = useImportFileTemplate()
    const [inactiveClient, setInactiveClient] = useState<boolean>(false)
    const defaultValues: IFormUpload = {
      uploadAccountFile: null,
      clientId: null,
      selectAction: EnumSelectAction.create,
    }
    const {
      control,
      formState: { errors, dirtyFields },
      handleSubmit,
      setValue,
      clearErrors,
      setError,
      reset,
      resetField,
      getValues,
    } = useForm<any>({
      mode: 'all',
      defaultValues,
    })

    const isSaveButtonDisabled =
      !!Object.keys(errors)?.length ||
      !Object.keys(dirtyFields)?.length ||
      isFileContainsInvalidData ||
      !getValues('clientId') ||
      !getValues('uploadAccountFile')

    const uploadAccountFile = useWatch({
      control,
      name: 'uploadAccountFile',
    })

    const clientIdWatch = useWatch({
      control,
      name: 'clientId',
    })

    const handleCloseModal = () => {
      setIsShowModal(false)
    }

    const handleOpenModal = () => {
      setIsShowModal(true)
    }

    const onSubmit = async (data: IFormUpload) => {
      const { selectAction } = getValues()
      try {
        const res: any = await importFileAccountTemplate(data)
        if (res) {
          /*IMPORT SUCCESSFULLY*/
          if (res?.statusCode === 'PPP30') {
            setError('clientId', { message: t(`SystemMessages:${res?.statusCode}`) })
          } else if (res?.statusCode === 'PPP24' || res?.statusCode === 'PPP25') {
            setIsResetView?.(true)
            setIsShowModal(false)
            notification.success({
              message: t(
                `SystemMessages:${
                  selectAction === EnumSelectAction.edit
                    ? SYSTEM_MESSAGE_IDS.ACCOUNT_UPLOAD_UPDATED_FROM_UPLOAD_SUCCESSFULLY
                    : SYSTEM_MESSAGE_IDS.ACCOUNT_UPLOAD_CREATED_FROM_UPLOAD_SUCCESSFULLY
                }`,
              ),
              closeIcon: null,
            })
          } else if (res?.statusCode === 'PPP04') {
            if (res && res?.dataImportProcessing.processedItems?.length > 0) {
              setIsResetView?.(true)
            }
            setIsFileContainsInvalidData(true)
          }
        }
      } catch (e) {
        console.log('ERROR: ', e)
      }
    }

    const handleBeforeUpload = async (file: any) => {
      setIsFileContainsInvalidData(false)

      const s30sTimeout = setTimeout(() => {
        if (!file) {
          setError('uploadAccountFile', {
            type: 'validation',
            message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.FILE_UPLOAD_TIMEOUT}`),
          })
          return false
        }
      }, 30000)

      const errorMes = validateFile(file, t)
      if (file) {
        setValue('uploadAccountFile', file)
        clearTimeout(s30sTimeout)
      }
      if (errorMes) {
        setError('uploadAccountFile', { type: 'validation', message: errorMes })
        return false
      } else {
        clearErrors('uploadAccountFile')
      }
    }

    const handleDownloadTemplate = async () => {
      try {
        setLoading(true)
        const data = await (await fetch(downloadFileURL)).arrayBuffer()
        const dataBlob = new Blob([new Uint8Array(data)])
        saveAs(dataBlob, `${templateFileName}.xlsx`)
        if (data) {
          setTimeout(() => {
            setLoading(false)
          }, 1500)
        }
      } catch (e) {
        console.log('ERROR', e)
        setTimeout(() => {
          setLoading(false)
        }, 1500)
      }
    }

    const resetForm = () => {
      reset()
      setIsFileContainsInvalidData(false)
    }

    useEffect(() => {
      const { selectAction, clientId } = getValues()
      setLoading(true)
      if (clientId) {
        getccountTemplate({ clientId, generateType: selectAction === EnumSelectAction.edit ? 1 : 0 })
          .then((res) => {
            setTemplateFileName(res.data.fileName)
            setDownloadFileURL(res.data.template)
            setTimeout(() => {
              setLoading(false)
            }, 1500)
          })
          .catch((e) => {
            console.log('ERROR', e)
            setTimeout(() => {
              setLoading(false)
            }, 1500)
          })
        resetField('uploadAccountFile')
        clearErrors('uploadAccountFile')
        setIsFileContainsInvalidData(false)
      }
    }, [getValues('selectAction'), getValues('clientId')])

    useEffect(() => {
      if (isShowModal || inactiveClient) {
        getClientOptions().then((res: any) => {
          clearErrors('clientId')
        })
        setInactiveClient(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowModal, inactiveClient])

    useImperativeHandle(
      ref,
      () => {
        return {
          openModal() {
            handleOpenModal()
          },
          closeModal() {
            handleCloseModal()
          },
          resetForm() {
            resetForm()
          },
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    )

    return (
      <CommonModal
        title={t('AccountSetup:uploadFile.uploadFile')}
        open={isShowModal}
        okText={t('AccountSetup:uploadFile.create')}
        okButtonProps={{
          disabled: isSaveButtonDisabled || isLoadingUploadLocal,
          style: { width: 102, fontWeight: 600 },
          size: 'middle',
          loading: isLoadingSubmitForm,
        }}
        cancelText={t('AccountSetup:uploadFile.cancel')}
        cancelButtonProps={{
          style: { width: 102, fontWeight: 600 },
          size: 'middle',
        }}
        onOk={async () => {
          handleSubmit(onSubmit)()
        }}
        onCancel={handleCloseModal}
        confirmLoading={false}
        onClose={handleCloseModal}
        width={950}
        maskClosable={false}
      >
        <Form
          name='account-setup-upload-form'
          className='form-container account-setup-upload-form'
          layout='vertical'
          style={{ padding: 0 }}
          onFinish={handleSubmit(onSubmit)}
        >
          <Flex vertical>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item label={t('AccountSetup:uploadFile.clientName')} className='isRequired'>
                  <Controller
                    name='clientId'
                    control={control}
                    rules={rules.clientName}
                    render={({ field, fieldState }) => (
                      <CustomSelect
                        {...field}
                        status={fieldState.error ? 'error' : ''}
                        placeholder={t('AccountSetup:uploadFile.selectClientName')}
                        options={!isLoadingClient ? clientOptions : []}
                        loading={isLoadingClient}
                        showSearch
                        defaultActiveFirstOption
                        onChange={(value) => {
                          field.onChange(value)
                          resetField('selectAction')
                          if (errors?.clientId) {
                            setInactiveClient(true)
                          }
                        }}
                      />
                    )}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='clientId' />
                  </Typography>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item label={t('AccountSetup:uploadFile.selectAction')} className='isRequired'>
                  <Controller
                    name='selectAction'
                    control={control}
                    render={({ field }) => (
                      <Radio.Group {...field} name={'selectAction'} defaultValue={EnumSelectAction.create}>
                        <Space direction='vertical'>
                          <Radio value={EnumSelectAction.create}>
                            <Typography.Text style={{ fontSize: 16, fontWeight: 400 }}>
                              {t('AccountSetup:uploadFile.uploadAndCreate')}
                            </Typography.Text>
                          </Radio>
                          <Radio value={EnumSelectAction.edit}>
                            <Typography.Text style={{ fontSize: 16, fontWeight: 400 }}>
                              {t('AccountSetup:uploadFile.uploadAndEdit')}
                            </Typography.Text>
                          </Radio>
                        </Space>
                      </Radio.Group>
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Flex>

          {clientIdWatch && (
            <Flex vertical>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label={t('AccountSetup:uploadFile.downloadAccountFileTemplate')}
                    style={{ marginBottom: 16 }}
                  >
                    <Controller
                      name='template'
                      control={control}
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          status={fieldState.error ? 'error' : ''}
                          className='sample-file-upload ant-input-view-only download-tmp'
                          value={templateFileName}
                          suffix={
                            loading ? (
                              <Spin indicator={<LoadingOutlined spin />} style={{ width: 24, height: 24 }} />
                            ) : (
                              <img src={DownloadWhiteIcon} alt='download ach icon' onClick={handleDownloadTemplate} />
                            )
                          }
                          readOnly
                        />
                      )}
                    />
                    <Typography className='error-message'>
                      <ErrorMessage errors={errors} name='template' />
                    </Typography>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item label={t('AccountSetup:uploadFile.uploadAccountFile')} className='isRequired'>
                    {uploadAccountFile ? (
                      <Controller
                        name='uploadAccountFile'
                        control={control}
                        rules={rules.uploadAccountFile}
                        render={({ field }) => (
                          <Flex
                            style={{
                              minHeight: 66,
                              border: `1px solid ${
                                isFileContainsInvalidData || errors.uploadAccountFile ? colorBgErrorBold : colorBorder
                              }`,
                              borderRadius: 12,
                              backgroundColor:
                                isFileContainsInvalidData || errors.uploadAccountFile ? colorBgError : '',
                            }}
                          >
                            {isLoadingUploadLocal ? (
                              <Flex justify={'center'} align={'center'} style={{ width: '100%' }}>
                                <Spin indicator={<LoadingOutlined spin />} />
                                <Typography.Text style={{ fontSize: 14, marginLeft: 8 }}>Uploading ...</Typography.Text>
                              </Flex>
                            ) : (
                              <Flex
                                justify={'space-between'}
                                align={'center'}
                                style={{
                                  width: '100%',
                                  minHeight: 66,
                                  padding: '0px 16px',
                                }}
                              >
                                <Flex justify={'flex-start'} align={'center'} style={{ maxWidth: 740 }}>
                                  <DownloadSuccessAccountIcon style={{ minWidth: 40 }} />
                                  <Flex vertical style={{ padding: 12 }}>
                                    <Typography.Text>{uploadAccountFile && uploadAccountFile.name}</Typography.Text>
                                    <Typography.Text
                                      style={{
                                        fontSize: 14,
                                        color: colorTextSubtlest,
                                      }}
                                    >
                                      {uploadAccountFile && (uploadAccountFile.size * 0.001).toFixed(2)}KB
                                    </Typography.Text>
                                  </Flex>
                                </Flex>

                                <Upload
                                  {...field}
                                  showUploadList={false}
                                  accept={fileExtension.join(',')}
                                  beforeUpload={handleBeforeUpload}
                                  onChange={(info) => {
                                    setIsLoadingUploadLocal(true)

                                    setTimeout(() => {
                                      setIsLoadingUploadLocal(false)
                                      setValue('uploadAccountFile', info.file)
                                    }, 1000)
                                  }}
                                >
                                  <Typography.Text
                                    style={{
                                      color: colorTextSelected,
                                      textDecoration: 'underline',
                                      fontSize: 14,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    Choose Another File
                                  </Typography.Text>
                                </Upload>
                              </Flex>
                            )}
                          </Flex>
                        )}
                      />
                    ) : (
                      <Controller
                        name='uploadAccountFile'
                        control={control}
                        rules={rules.uploadAccountFile}
                        render={({ field }) => (
                          <Row>
                            <Col span={24}>
                              <Dragger
                                {...field}
                                showUploadList={false}
                                accept={fileExtension.join(',')}
                                beforeUpload={handleBeforeUpload}
                                onChange={(info) => {
                                  setIsLoadingUploadLocal(true)

                                  setTimeout(() => {
                                    setIsLoadingUploadLocal(false)
                                    setValue('uploadAccountFile', info.file)
                                  }, 1000)
                                }}
                              >
                                <Flex style={{ height: 48 }} justify={'center'}>
                                  {isLoadingUploadLocal ? (
                                    <Flex justify={'center'} align={'center'}>
                                      <Spin indicator={<LoadingOutlined spin />} />
                                      <Typography.Text style={{ fontSize: 14, marginLeft: 8 }}>
                                        {t('AccountSetup:uploadFile.uploading')}
                                      </Typography.Text>
                                    </Flex>
                                  ) : (
                                    <Space>
                                      <UploadAccountIcon />
                                      <Typography.Text
                                        style={{
                                          marginLeft: 12,
                                          fontSize: 14,
                                          fontWeight: 400,
                                          color: colorText,
                                        }}
                                      >
                                        {t('AccountSetup:uploadFile.dragAndDrop')}
                                      </Typography.Text>
                                      <Typography.Text
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 400,
                                          color: colorTextSelected,
                                          textDecoration: 'underline',
                                        }}
                                      >
                                        {t('AccountSetup:uploadFile.chooseFile')}
                                      </Typography.Text>
                                    </Space>
                                  )}
                                </Flex>
                              </Dragger>
                            </Col>
                            <Col span={24} style={{ marginTop: 10 }}>
                              <Flex justify={'space-between'}>
                                <Typography.Text
                                  style={{
                                    fontSize: 14,
                                    color: colorTextSubtle,
                                  }}
                                >
                                  {t('AccountSetup:uploadFile.fileSupport')}
                                </Typography.Text>
                                <Typography.Text
                                  style={{
                                    fontSize: 14,
                                    color: colorTextSubtle,
                                  }}
                                >
                                  {t('AccountSetup:uploadFile.maxSize')}
                                </Typography.Text>
                              </Flex>
                            </Col>
                          </Row>
                        )}
                      />
                    )}
                    <Typography className='error-message'>
                      <ErrorMessage errors={errors} name='uploadAccountFile' />
                    </Typography>
                    {isFileContainsInvalidData && (
                      <Typography className='error-message'>
                        <Typography.Text style={{ color: colorBgErrorBold }}>
                          {t('AccountSetup:uploadFile.fileContainsInvalidDate')}
                        </Typography.Text>
                        <Typography.Text
                          style={{
                            color: '#004B91',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontWeight: 400,
                          }}
                          onClick={() => onViewErrorResults && onViewErrorResults(resImportFileTemplate as any)}
                        >
                          {t('AccountSetup:uploadFile.viewErrorData')}
                        </Typography.Text>
                      </Typography>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Flex>
          )}
        </Form>
      </CommonModal>
    )
  },
)

export default AccountSetupUpload
